// Almaty, "Zenkov under the snowy peaks": a frosty winter night in Panfilov Park (the Park of 28 Panfilov Guardsmen),
// on the alley that runs north from the Ascension Cathedral, looking south (bearing about 172°) to the Trans-Ili Alatau.
// Where we stand: 43.2615 N, 76.9526 E, some 300 m north of the cathedral and 40 m west of its axis. Left to right, as
// seen from there: Kok-Tobe hill with its amusement park and wheel (bearing 148°) and the 372 m TV tower (152°), the
// gondola climbing to it from Abay Avenue, Hotel Kazakhstan's crown (170°), the cathedral's north front, its apse on the
// left (east), the five domes, the bell tower on the right (west) (167–178°), Big Almaty Peak's pyramid (184°) and the
// lower ridges to the south-west. The snow wall of the Alatau stands behind all of it, highest on the left (Almaty Peak)
// and dipping behind the cathedral.
// Cheats, as through a long lens: the angles across are pressed into the frame without changing the order, the range
// is drawn about twice as tall as the 7–8.5° it rises from here, the cathedral stands nearer than 300 m, and Hotel
// Kazakhstan, which stands right behind the apse from here, is pulled left so it shows.
// Back to front: deep winter sky, stars, the moon, thin moonlit cloud -> the Alatau's snow peaks with cloud shadows
// drifting over them, the forested front range, a mist on the foothills -> Kok-Tobe, the TV tower, the wheel, the
// gondola -> the city's lights -> Hotel Kazakhstan -> the park's bare trees and spruces, a Soviet block beyond the park
// carrying the billboard -> the Ascension Cathedral (Zenkov's wooden cathedral, 1907), floodlit -> the square and the
// alley, cleared and wet down its middle between snowbanks, its candelabra lamps -> the tea kiosk on the left (the cozy
// corner) with its queue, a bench, a snowman -> people -> snow at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HZ = 178;                                   // the cathedral's foot, the square in front of it
const FEET = 206;                                 // the near row of people (above the bottom 63 rows)
const MOON = { x: 200, y: 21, r: 8 };
// The Ascension Cathedral, broadside from the north: 56 m long and 56 m to the bell tower's cross, drawn 158 x 150 px.
// East end (apse) on the left at x0, west end (the low wing behind the bell tower) on the right at x1.
const CH = 150, CL = 158;
const CA = { x0: 222, x1: 222 + CL - 1, base: HZ, tip: HZ - CH };
const fy = f => Math.round(HZ - f * CH);          // a height given as a share of the cathedral's
const EAVE = fy(0.36), MID = fy(0.18);            // the walls' cornice (124) and the first storey's (151)
const BODY = { x0: 232, x1: 322 };                // the two-storey main body between the apse and the bell tower
const MAIN = { x: 276, hw: 18 };                  // the central drum, its dome, lantern, onion and cross
const CORNERS = [246, 307];                       // the two north corner domes (drums 20 px, clear of the central one)
const BELL = { x: 334, hw: 12 };                  // the bell tower, its cross at CA.tip
const TV = { x: 128, top: 30, foot: 108 };        // Kok-Tobe TV tower on its hilltop
const WHEEL = { x: 72, y: 98, r: 6 };             // Kok-Tobe's wheel on the crest
const HOTEL = { x: 196, top: 100, hw: 6 };        // Hotel Kazakhstan, its crown at top
const PEAK = { x: 430, y: 31 };                   // Big Almaty Peak's apex
const BB = { x: 392, y: 108, w: 52, h: 20 };      // the billboard on the block's roof
const BLOCK = { x0: 384, roof: 132 };             // the Soviet block beyond the park, right
const KIOSK = { x0: 46, x1: 122, top: 150, base: 206 };   // the tea kiosk (the cozy corner)
// the alley: from the porch down to the left, widening towards us; its middle swept and wet
const PATH = { y0: 184, x0: 276, x1: 150, w0: 13, w1: 54 };
const pathC = y => lerp(PATH.x0, PATH.x1, (y - PATH.y0) / (H - PATH.y0));
const pathW = y => lerp(PATH.w0, PATH.w1, (y - PATH.y0) / (H - PATH.y0));

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  return { c, x, put, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function bres(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], clamp(t, 0, 1))).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
// smooth value noise (build time only)
function vnoise(x, y, c, s) {
  const gx = Math.floor(x / c), gy = Math.floor(y / c), fx = x / c - gx, fy2 = y / c - gy;
  const sx = fx * fx * (3 - 2 * fx), sy = fy2 * fy2 * (3 - 2 * fy2);
  return lerp(lerp(hash(gx, gy, s), hash(gx + 1, gy, s), sx), lerp(hash(gx, gy + 1, s), hash(gx + 1, gy + 1, s), sx), sy);
}
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
// a full-frame layer trimmed to what it holds (build time), and drawn as that box only
function trim(c) {
  const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
  let x0 = c.width, y0 = c.height, x1 = -1, y1 = -1;
  for (let y = 0; y < c.height; y++) for (let x = 0; x < c.width; x++) if (d[(y * c.width + x) * 4 + 3]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  return x1 < 0 ? { c, x: 0, y: 0, w: 1, h: 1 } : { c, x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y, o.w, o.h, o.x, o.y, o.w, o.h);
function drawScroll(cv, y, k, t) {                // a tileable band drifting k (integer) widths a loop
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}

/* ================= Sign font: FONT3 with wide M, W, N, K and the Cyrillic letters the signs spell ================= */
// Kazakh words only: ШАЙ (tea), КОФЕ (coffee), АЛМА (apple), АПОРТ (the Almaty apple), ШЫРЫН (juice). Й's breve sits a
// row above the cap line.
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
  'К': ['1001', '1010', '1100', '1010', '1001'], 'М': ['10001', '11011', '10101', '10001', '10001'],
  'П': ['1111', '1001', '1001', '1001', '1001'], 'Л': ['0111', '0101', '0101', '0101', '1101'],
  'Ш': ['10101', '10101', '10101', '10101', '11111'], 'Ы': ['10001', '10001', '11101', '10101', '11101'],
  'Й': ['0110', '0000', '1001', '1011', '1101', '1001'], 'Ф': ['00100', '01110', '10101', '01110', '00100'],
  'Н': ['101', '101', '111', '101', '101'], 'А': FONT3.A, 'О': FONT3.O, 'Р': FONT3.P, 'Т': FONT3.T, 'Е': FONT3.E,
  '·': ['000', '000', '010', '000', '000'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y) - (g.length - 5), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: a clear frosty night, the moon over the dip in the range, thin moonlit cloud ================= */
const SKY = ['#03061a', '#050a22', '#070e2a', '#0a1332', '#0d183b', '#111e44', '#16254d', '#1c2c56', '#23345e', '#2b3b64'];
function renderSky() {
  return ditherFill(W, 150, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 110), 1.4) * 0.8;
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 100, (y - MOON.y) / 60)) * 0.18;
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.9], [2, 2, 1.6], [2, -3, 1.2], [-1, 4, 1], [4, 1, 0.9]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -6 ? '#fffdf2' : '#f0f0e6';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c6c8c4' : '#e2e4dc';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#b8bab8' : '#d2d4cc';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(55);
  while (STARS.length < 70) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 56);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 20) continue;
    STARS.push({ x, y, k: 12 + Math.floor(r() * 50), ph: r(), b: 0.45 + r() * 0.55, big: r() < 0.1 });
  }
}
// thin moonlit wisps, no outlines, dithered into the sky; tileable so they drift
const WISPS = [{ seed: 4, y: 2, h: 13, n: 2, k: 1 }, { seed: 7, y: 14, h: 15, n: 3, k: 2 }, { seed: 12, y: 30, h: 18, n: 3, k: 1 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = 50 + r() * 80;
    for (let k = 0; k < 6; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.4 + r() * 0.25), rx: 12 + r() * 18, ry: 1.6 + r() * 2 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    if (v > 0.55) p.put(x, y, v > 0.8 ? '#323e70' : '#28336a');
    else if (v > 0.2 && (v - 0.2) * 2.6 > bayer(x, y)) p.put(x, y, '#1e2858');
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The Trans-Ili Alatau: moonlit snow peaks over the forested front range ================= */
// The skyline as seen from the park, from the real summits' elevations (OpenStreetMap): Almaty Peak's massif at the left
// edge, Kumbel over Kok-Tobe, the dip behind the cathedral, Big Almaty Peak's pyramid over the saddles either side of it.
// Drawn about twice as tall as it stands, as through a long lens.
const CREST_PTS = [[-4, 30], [0, 30], [14, 25], [30, 31], [44, 27], [58, 33], [72, 30], [88, 37], [102, 34], [116, 41],
  [130, 40], [146, 36], [160, 32], [174, 39], [188, 47], [200, 53], [214, 50], [228, 46], [242, 51], [258, 56], [274, 60],
  [292, 58], [312, 62], [332, 58], [352, 55], [370, 59], [388, 62], [400, 55], [412, 45], [422, 36], [430, 31], [437, 37],
  [445, 47], [454, 58], [464, 64], [474, 58], [486, 52]];
const CREST = new Float32Array(W + 8);
{
  let k = 1;
  for (let x = -4; x < W + 4; x++) {
    while (CREST_PTS[k][0] < x) k++;
    const [a, ya] = CREST_PTS[k - 1], [b, yb] = CREST_PTS[k], u = (x - a) / (b - a);
    const jag = Math.abs(x - PEAK.x) < 16 ? 0 : (hash(x >> 1, 3, 91) - 0.5) * 1.4 + (hash(x >> 2, 5, 92) - 0.5) * 1.8;
    CREST[x + 4] = lerp(ya, yb, u) + jag;
  }
}
const crestY = x => CREST[clamp(Math.round(x), -4, W + 3) + 4];
const ridge2 = x => 100 + (vnoise(x + 40, 0, 46, 201) - 0.5) * 16 + (vnoise(x, 0, 14, 202) - 0.5) * 5;
const foot = x => 125 + (vnoise(x, 0, 34, 203) - 0.5) * 9 + (vnoise(x, 0, 9, 204) - 0.5) * 3;
const SNOWP = ['#3a4676', '#4e5c8a', '#6676a4', '#8292be', '#a0b0d6', '#bccae6'];   // snow from deep shade to moonlit
function renderMountains() {
  const p = painter(W, H), put = p.put;
  // the crest's slope, smoothed more the further down, so each summit sends a spur down: faces turned left catch the
  // moon, faces turned right are in shade; rock bands and couloirs break the snowfields up
  const S = new Float32Array(W + 1);
  for (let x = 0; x < W; x++) S[x + 1] = S[x] + (crestY(x + 2) - crestY(x - 2));
  const slopeAt = (x, r) => { const a = clamp(Math.round(x - r), 0, W - 1), b = clamp(Math.round(x + r), a + 1, W); return (S[b] - S[a]) / (b - a); };
  for (let x = 0; x < W; x++) {
    const c = Math.round(crestY(x));
    for (let y = c; y < 140; y++) {
      const d = y - c, drift = (vnoise(x, y, 13, 95) - 0.5) * d * 0.8;
      let lit = -slopeAt(x + drift, 1.5 + d * 0.42) * 0.5 + 0.56 + (vnoise(x, y, 8, 93) - 0.5) * 0.3 + (hash(x, y, 99) - 0.5) * 0.07 - d * 0.004;
      const rock = vnoise(x + d * 0.5, y * 1.5, 6, 94) > 0.84 && d > 4;
      let col = rock ? (lit > 0.55 ? '#56628e' : '#3e4874') : ramp(SNOWP, lit, x, y);
      if (d === 0) col = lit > 0.45 ? '#c2cee8' : '#8a9ac4';        // the moon catching the ridge line
      put(x, y, mixHex(col, '#2a3460', smooth(30, 80, d) * 0.62));   // the lower slopes fade into the city's glow
    }
  }
  // the front range: dark spruce forest with snowy clearings
  for (let x = 0; x < W; x++) for (let y = Math.round(ridge2(x)); y < 150; y++) {
    const d = y - ridge2(x), clear = vnoise(x, y * 1.4, 6, 96), n = hash(x, y, 97);
    const col = d < 1.2 ? '#5a668e' : clear > 0.64 ? (n < 0.6 ? '#4a567e' : '#3e4a72') : n < 0.15 ? '#34406a' : n < 0.6 ? '#252e4e' : '#202846';
    put(x, y, mixHex(col, '#262f54', smooth(8, 30, d) * 0.55));
  }
  // the foothills in front, darker, a few houses lit on their slopes
  for (let x = 0; x < W; x++) for (let y = Math.round(foot(x)); y < 160; y++) {
    const d = y - foot(x), n = hash(x, y, 98);
    put(x, y, d < 1 ? '#323c62' : vnoise(x, y, 5, 99) > 0.66 ? '#2c3456' : n < 0.5 ? '#1c2240' : '#191e3a');
  }
  const r = rng(98);
  for (let k = 0; k < 20; k++) {
    const x = 150 + Math.floor(r() * 330), y = Math.round(foot(x)) + 3 + Math.floor(r() * 12);
    put(x, y, r() < 0.7 ? '#e8b060' : '#f4d898');
  }
  return p.done();
}
// the range's outline as a clip path, so the cloud shadows fall on the mountains only
function mountainPath() {
  const path = new Path2D();
  path.moveTo(0, 150); path.lineTo(0, crestY(0));
  for (let x = 1; x <= W; x++) path.lineTo(x, crestY(x));
  path.lineTo(W, 150); path.closePath();
  return path;
}
// cloud shadows drifting over the snowfields (tileable, three soft levels), and the mist lying on the foothills
function renderBlobs(h, n, seed, col, levels, ry0) {
  const [c, x] = makeCanvas(W, h), r = rng(seed), blobs = [], [cr, cg, cb] = hex(col);
  for (let k = 0; k < n; k++) blobs.push({ x: r() * W, y: ry0 + r() * (h - 2 * ry0), rx: 44 + r() * 56, ry: ry0 * (0.6 + r() * 0.5) });
  const img = x.createImageData(W, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    let v = 0;
    for (const b of blobs) { let dx = i - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    const a = v > 0.55 ? levels[2] : v > 0.28 ? levels[1] : v > 0.08 ? levels[0] : 0, o = (y * W + i) * 4;
    if (!a) continue;
    d[o] = cr; d[o + 1] = cg; d[o + 2] = cb; d[o + 3] = Math.round(a * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Kok-Tobe: the hill, its amusement park on the crest, the wheel and the TV tower ================= */
const kokY = x => x < 150 ? 108 + 1.5 * Math.sin(x * 0.1) : 108 + Math.pow(Math.min(1, (x - 150) / 84), 1.5) * 36;
function renderKokTobe() {
  const p = painter(W, H), put = p.put;
  for (let x = 0; x < 236; x++) for (let y = Math.round(kokY(x)); y < 168; y++) {
    const d = y - kokY(x), n = hash(x, y, 101), path = Math.abs(((x * 0.5 + y * 1.3) % 23) - 11) < 0.6 && d > 4;
    let col = d < 1 ? '#5a6488' : n < 0.25 ? '#3a4266' : n < 0.62 ? '#2a3052' : '#242a48';
    if (path) col = '#4a5276';
    put(x, y, mixHex(col, '#3a3050', smooth(6, 34, d) * 0.35));
  }
  const r = rng(102);                                       // houses down the slope (the Kok-Tobe district)
  for (let k = 0; k < 18; k++) {
    const x = 4 + Math.floor(r() * 222), y = Math.round(kokY(x)) + 5 + Math.floor(r() * 22), w = 3 + Math.floor(r() * 3);
    if (y > 160) continue;
    for (let i = x; i < x + w; i++) { put(i, y, '#3e3a58'); put(i, y + 1, '#2a2840'); }
    if (r() < 0.8) put(x + 1, y + 1, r() < 0.7 ? '#f0b868' : '#ffe0a0');
  }
  for (const [x0, w, h] of [[12, 9, 3], [48, 7, 2], [88, 5, 4], [100, 8, 2], [140, 6, 3]]) {   // pavilions on the crest
    const g = Math.round(kokY(x0 + (w >> 1)));
    for (let y = g - h; y < g; y++) for (let i = x0; i < x0 + w; i++) put(i, y, y === g - h ? '#8a7a7a' : '#3a3450');
  }
  return p.done();
}
// the tower, after the photos: a tubular shaft on a flared foot, a ring at 22 %, the broad platform at 46 %, the cabin
// pod at 65 %, then the antenna mast (the top 32 %), banded red and white; floodlit warm white
const TVS = { lt: '#f4e8d2', md: '#cdbba2', sh: '#8a7c70', red: '#d85a4a' };
const tvAt = f => Math.round(TV.foot - f * (TV.foot - TV.top));
function renderTower() {
  const p = painter(W, H), put = p.put, { x, top, foot } = TV;
  for (let y = tvAt(0.68); y <= foot; y++) { put(x - 1, y, TVS.lt); put(x, y, TVS.md); put(x + 1, y, TVS.sh); }
  for (let y = foot - 4; y <= foot; y++) { put(x - 2, y, TVS.md); put(x + 2, y, TVS.sh); }
  put(x - 3, foot, TVS.sh); put(x + 3, foot, TVS.sh); put(x - 3, foot - 1, TVS.sh); put(x + 3, foot - 1, TVS.sh);
  for (const f of [0.22, 0.59]) for (let i = x - 2; i <= x + 2; i++) put(i, tvAt(f), i <= x ? TVS.lt : TVS.sh);
  const plat = tvAt(0.46);
  for (let i = x - 5; i <= x + 5; i++) { put(i, plat, i <= x ? TVS.lt : TVS.md); put(i, plat + 1, TVS.sh); }
  for (let i = x - 4; i <= x + 4; i++) put(i, plat - 1, TVS.md);
  const pod = tvAt(0.65);
  for (let y = pod - 1; y <= pod + 2; y++) for (let i = x - 4; i <= x + 4; i++) put(i, y, y === pod ? (i & 1 ? '#ffe0a0' : TVS.md) : i <= x ? TVS.lt : TVS.sh);
  for (let y = top; y < tvAt(0.68); y++) {
    const band = Math.floor((y - top) / 3) & 1;
    put(x, y, band ? TVS.red : TVS.lt);
    if (y > top + 10) put(x - 1, y, band ? '#a84438' : TVS.md);
  }
  return p.done();
}

/* ================= The city beyond the park, and Hotel Kazakhstan ================= */
const CITY_WINS = [];                                     // windows that switch on and off (lit per frame)
function renderCity() {
  const p = painter(W, H), put = p.put, r = rng(111);
  CITY_WINS.length = 0;
  let x = -6;
  while (x < W) {
    const w = 9 + Math.floor(r() * 14), tall = r() < 0.16, top = tall ? 128 + Math.floor(r() * 6) : 140 + Math.floor(r() * 12);
    const tone = r() < 0.5 ? ['#2e2c4a', '#252440', '#3c3a5c'] : ['#2a3048', '#22283e', '#384266'];
    for (let y = top; y < HZ + 4; y++) for (let i = x; i < x + w; i++) put(i, y, y === top || i === x ? tone[2] : i === x + w - 1 ? tone[1] : tone[0]);
    for (let y = top + 2; y < HZ; y += 3) for (let i = x + 2; i < x + w - 2; i += 2) {
      const n = hash(i, y, 112);
      if (n < 0.16) put(i, y, n < 0.12 ? '#e8b468' : '#f6dca0');
      else if (n < 0.2) put(i, y, '#8aa0d0');
      else if (n < 0.26) CITY_WINS.push({ x: i, y, ph: hash(i, y, 113), k: 2 + Math.floor(hash(i, y, 114) * 4) });
    }
    x += w + (r() < 0.3 ? 2 : 0);
  }
  // Hotel Kazakhstan: a white slab with a bowed middle and its crown of spikes on a zigzag cornice, the crown gold
  const { x: hx, top: ht, hw } = HOTEL, body = ht + 6;
  for (let y = body; y < 158; y++) for (let i = hx - hw; i <= hx + hw; i++) {
    const mid = Math.abs(i - hx) <= 2;
    let col = i === hx - hw ? '#9a9cb0' : i === hx + hw ? '#4a4c64' : mid ? '#7c7e96' : '#626480';
    if ((y - body) % 2 === 1 && i > hx - hw && i < hx + hw && (i - hx + 8) % 2 === 0) { const n = hash(i, y, 121); col = n < 0.42 ? (n < 0.3 ? '#f2c878' : '#ffe6a8') : '#343650'; }
    put(i, y, col);
  }
  for (let i = hx - hw - 1; i <= hx + hw + 1; i++) { put(i, body - 1, i & 1 ? '#e8dcc0' : '#c8b896'); put(i, body - 2, '#b8aa90'); }
  for (let i = hx - hw; i <= hx + hw; i++) {
    const tall = (i - hx + 10) % 2 === 0, h = tall ? 4 : 2;
    for (let y = body - 2 - h; y < body - 2; y++) put(i, y, y === body - 2 - h ? '#fff0b8' : tall ? '#f2c860' : '#c89a48');
  }
  return p.done();
}

/* ================= The park's trees: bare elms and oaks with snow on their limbs, Tian Shan spruces ================= */
function bareTree(p, x0, base, h, seed, dk = 0) {
  const r = rng(seed), put = p.put, bark = mixHex('#2c2638', '#141830', dk), lit = mixHex('#7a84aa', '#2a3050', dk);
  const twig = mixHex('#3a3448', '#1a1e36', dk);
  const limb = (x, y, ang, len, w, depth) => {
    const x1 = x + Math.cos(ang) * len, y1 = y - Math.sin(ang) * len, n = Math.max(1, Math.round(len));
    for (let k = 0; k <= n; k++) {
      const u = k / n, px = lerp(x, x1, u), py = lerp(y, y1, u);
      for (let j = 0; j < w; j++) put(px + j - (w >> 1), py, bark);
      if (depth < 4 && hash(Math.round(px), Math.round(py), seed) < 0.55) put(px, py - 1, lit);   // snow along the limb
    }
    if (depth >= 5 || len < 3) { for (let k = 0; k < 5; k++) put(x1 + (r() - 0.5) * 5, y1 - r() * 4, twig); return; }
    const kids = depth < 2 ? 3 : 2;
    for (let k = 0; k < kids; k++) limb(x1, y1, ang + (r() - 0.5) * 1.3 + (k - (kids - 1) / 2) * 0.5, len * (0.62 + r() * 0.16), Math.max(1, w - 1), depth + 1);
  };
  limb(x0, base, Math.PI / 2 + (r() - 0.5) * 0.15, h * 0.36, 2, 0);
}
// a spruce: drooping tiers of uneven depth, snow lying in patches on each bough, ragged tips
function spruce(p, x, base, h, seed, dk = 0) {
  const put = p.put, r = rng(seed);
  const G = ['#12221c', '#1a3026', '#244232'].map(c => mixHex(c, '#141a30', dk)), S = ['#c4cee8', '#94a0c4', '#6a7698'].map(c => mixHex(c, '#2a3050', dk));
  const top = base - h, tiers = 6 + Math.floor(r() * 3), off = [0];
  for (let k = 1; k < tiers; k++) off.push(k / tiers + (r() - 0.5) * 0.06);
  off.push(1);
  for (let y = top; y < base; y++) {
    const u = (y - top) / h;
    let k = 0; while (k < tiers - 1 && u >= off[k + 1]) k++;
    const tu = (u - off[k]) / (off[k + 1] - off[k]), hw = Math.round((1 + u * h * 0.25) * (0.55 + 0.45 * tu));
    for (let i = x - hw; i <= x + hw; i++) {
      const e = Math.abs(i - x) / Math.max(1, hw), n = hash(i, y, seed);
      if (e > 0.82 && n < 0.45) continue;
      let col = i < x ? G[1] : G[0];
      if (n < 0.1) col = G[2];
      if (tu < 0.32 && hash((i + k * 5) >> 2, k, seed + 1) < 0.62 && n < 0.88) col = i < x - 1 ? S[0] : i <= x + 1 ? S[1] : S[2];
      put(i, y, col);
    }
  }
  for (let y = base; y < base + 3; y++) { put(x, y, '#1e1a24'); put(x - 1, y, '#2a2430'); }
  put(x, top - 1, S[1]);
}
const BACK_TREES = [[150, 182, 46, 3], [174, 180, 52, 5], [206, 180, 40, 6], [362, 182, 36, 8], [404, 184, 46, 10], [452, 186, 40, 11], [110, 186, 44, 12]];
const SPRUCES = [[214, 181, 50, 21], [384, 181, 56, 22], [160, 186, 38, 23], [234, 182, 24, 25]];
function renderPark() {
  const p = painter(W, H);
  for (const [x, b, h, s] of BACK_TREES) bareTree(p, x, b, h, s, 0.35);
  for (const [x, b, h, s] of SPRUCES) spruce(p, x, b, h, s, 0.15);
  return p.done();
}
// rooks roosting in the bare tree by the block (they stay); others on the cathedral's roofs lift off at the bells
const ROOKS = [[398, 146], [404, 141], [411, 147], [416, 143], [395, 152]];

/* ================= The Ascension Cathedral: Zenkov's wooden cathedral, floodlit ================= */
// Real: 56 m long, 56 m to the bell tower's cross (OpenStreetMap footprint; Wikipedia). From the broadside photos: the
// central dome's cross stands at 0.90 of that, the corner domes' at 0.82, the walls' cornice at 0.36, the first-storey
// cornice at 0.18. Here 150 px: crosses at rows 28, 43 and 55, the eave at 124. Yellow walls, white pilasters and window
// surrounds, green iron roofs under snow; the central dome tiled in a red, blue and white harlequin over big
// kokoshniks, the corner domes in blue, green and white, gold onions and crosses; the slim checkered tent of the belfry.
const CW = ['#6e5236', '#a8823e', '#d4aa54', '#f0cf74', '#fde8a8'];
const TR = { hi: '#fffaf0', lt: '#efe6d2', sh: '#bcae96', dk: '#857868' };
const RF = { lt: '#66c2a2', md: '#3e9a80', dk: '#246858' };
const SNC = { hi: '#f4f6ff', lt: '#d6def2', sh: '#9aa6c8' };
const GD = { hi: '#fff6c8', lt: '#ffd66a', md: '#e2a640', sh: '#a87430', dk: '#6a4a26' };
const HARL = { r: '#d8464c', b: '#3c62c8', w: '#f0eee6', t: '#3aa08e', rs: '#9a3040', bs: '#2a4690', ws: '#b8b6c4', ts: '#2a7064' };
const CATH_WINS = [];                                      // windows whose candlelight flickers (drawn per frame)
function renderCathedral() {
  const p = painter(W, H), put = p.put, base = HZ;
  CATH_WINS.length = 0;
  const wallAt = (x, y, x0, x1, lift = 0) => {
    const u = (x - x0) / Math.max(1, x1 - x0), v = 0.55 + 0.24 * smooth(EAVE - 6, base, y) + 0.1 * Math.sin(Math.PI * u) + lift;
    return ramp(CW, v + (bayer(x, y) - 0.5) * 0.12 - (u > 0.9 ? 0.12 : 0), x, y);
  };
  const wall = (x0, x1, y0, y1, lift = 0) => { for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) put(x, y, wallAt(x, y, x0, x1, lift)); };
  const pilaster = (x, y0, y1) => { for (let y = y0; y <= y1; y++) { put(x, y, TR.hi); put(x + 1, y, TR.sh); } };
  const cornice = (x0, x1, y) => { for (let x = x0; x <= x1; x++) { put(x, y, TR.hi); put(x, y + 1, (x & 1) ? TR.sh : TR.lt); put(x, y + 2, CW[1]); } };
  const roofStrip = (x0, x1, y, rows = 3) => {
    for (let x = x0; x <= x1; x++) {
      put(x, y - rows, hash(x, y, 131) < 0.8 ? SNC.lt : SNC.hi);
      for (let k = 1; k < rows; k++) put(x, y - rows + k, k === 1 ? RF.lt : RF.md);
    }
  };
  const win = (x, y, w, h, k, arch = true) => {               // white surround, glass lit warm or dark
    const on = hash(x, y, 130 + k) < 0.55;
    for (let j = -1; j <= h; j++) for (let i = -1; i <= w; i++) {
      const frame = i < 0 || i >= w || j < 0 || j >= h;
      put(x + i, y + j, frame ? (i < 0 ? TR.hi : TR.lt) : on ? (j < 2 ? '#ffe6a8' : j < h - 2 ? '#ffcf7a' : '#e8a858') : (j < 1 ? '#5a4a52' : '#2e2434'));
    }
    if (w >= 3 && h > 5) for (let j = 0; j < h; j++) put(x + (w >> 1), y + j, on ? '#c88a48' : '#4a3c46');
    if (arch) { for (let i = 0; i < w; i++) put(x + i, y - 2, TR.lt); put(x + (w >> 1), y - 3, TR.hi); if (w > 3) put(x + (w >> 1) - 1, y - 3, TR.hi); }
    if (on && w >= 3) CATH_WINS.push({ x, y, w, h, ph: hash(x, y, 132) });
  };
  const harl = (x, y, cx, shade, c = 3, green = false) => {
    const a = Math.floor((x - cx + y + 64) / c), b = Math.floor((x - cx - y + 640) / c);
    const k = (a + b) & 1 ? 'w' : (a & 1) ? (green ? 't' : 'r') : 'b';
    return shade ? HARL[k + 's'] : HARL[k];
  };
  const onion = (cx, y0, y1, hw) => {
    for (let y = y0; y <= y1; y++) {
      const u = (y - y0) / (y1 - y0), r = hw * (u < 0.62 ? Math.pow(Math.sin(u / 0.62 * Math.PI / 2), 1.3) : lerp(1, 0.4, (u - 0.62) / 0.38));
      const rr = Math.max(0, Math.round(r));
      for (let x = cx - rr; x <= cx + rr; x++) {
        const s = (x - cx) / Math.max(1, rr);
        put(x, y, s < -0.5 ? GD.lt : s < 0.1 ? (u > 0.3 && u < 0.6 && s > -0.4 && s < -0.05 ? GD.hi : GD.lt) : s < 0.6 ? GD.md : GD.sh);
      }
    }
  };
  const cross = (cx, y0, y1) => {                                  // the Orthodox cross on its stem, gold
    for (let y = y0; y <= y1; y++) put(cx, y, GD.lt);
    put(cx - 1, y0 + 1, GD.md); put(cx + 1, y0 + 1, GD.md);
    for (let i = -2; i <= 2; i++) put(cx + i, y0 + 3, i < 0 ? GD.lt : GD.md);
    put(cx - 1, y0 + 5, GD.md); put(cx + 1, y0 + 6, GD.md);
  };
  const kokoshnik = (cx, y1, hw, fan) => {                         // a round gable: rim, red and pink bands, a fan, a window
    for (let y = y1 - hw; y <= y1; y++) for (let x = cx - hw; x <= cx + hw; x++) {
      const d = Math.hypot(x - cx, y - y1);
      if (d > hw + 0.3) continue;
      let col = d > hw - 1 ? TR.hi : CW[3];
      if (fan) {
        if (d <= hw - 1) col = d > hw - 2.2 ? HARL.r : d > hw - 3.3 ? '#f0a8b8' : d > hw - 4.3 ? TR.lt : (Math.floor(Math.atan2(y1 - y, x - cx) * 3.2) & 1 ? HARL.r : TR.lt);
        if (d < 1.3) col = '#e8a050';
      }
      put(x, y, col);
    }
  };
  // the apse (east end), a lower block with its own roof
  wall(CA.x0, BODY.x0, 140, base - 1);
  roofStrip(CA.x0 - 1, BODY.x0, 140, 4);
  win(CA.x0 + 3, 152, 3, 10, 1);
  // the main body, the bell tower's shaft from the ground, the low west wing behind it with its porch
  wall(BODY.x0, BODY.x1, EAVE, base - 1);
  wall(BELL.x - BELL.hw, BELL.x + BELL.hw, 97, base - 1, 0.03);
  wall(BELL.x + BELL.hw + 1, CA.x1, 148, base - 1, -0.04);
  roofStrip(BELL.x + BELL.hw + 1, CA.x1 + 1, 148, 4);
  for (let k = 0; k <= 9; k++) for (let x = CA.x1 - 18 + k; x <= CA.x1 - k; x++) put(x, 147 - k, x === CA.x1 - 18 + k ? SNC.lt : x === CA.x1 - k ? RF.dk : k === 0 ? TR.lt : RF.md);
  for (let x = CA.x0; x <= CA.x1; x++) for (let y = base - 4; y < base; y++) put(x, y, y === base - 4 ? TR.lt : (x + y) % 7 ? '#94463a' : '#7a3a30');   // the red plinth
  for (const x of [BODY.x0, 247, 262, 290, 305, BODY.x1 - 2]) pilaster(x, EAVE, base - 5);
  for (const x of [BELL.x - BELL.hw, BELL.x + BELL.hw - 1]) pilaster(x, 97, base - 5);
  cornice(BODY.x0, BELL.x + BELL.hw, MID);
  cornice(BODY.x0 - 1, BELL.x + BELL.hw + 1, EAVE);
  for (const wx of [238, 253, 296, 311]) { win(wx, 129, 4, 13, 2); win(wx, 156, 4, 11, 3); }
  win(BELL.x - 2, 129, 4, 13, 4); win(BELL.x - 2, 156, 4, 11, 5);
  for (const x of [352, 362]) win(x, 157, 3, 10, 6);
  // the north porch: a projecting gabled front with the door, under a pediment on the eave
  wall(265, 287, 146, base - 1, 0.08);
  pilaster(265, 146, base - 5); pilaster(286, 146, base - 5);
  for (let k = 0; k <= 12; k++) for (let x = 276 - 12 + k; x <= 276 + 12 - k; x++) put(x, 145 - k, k === 0 ? TR.lt : x === 276 - 12 + k ? SNC.lt : x === 276 + 12 - k ? RF.dk : k < 2 ? RF.md : CW[3]);
  for (let k = 2; k < 11; k++) { put(276 - 12 + k + 1, 145 - k, TR.hi); put(276 + 12 - k - 1, 145 - k, TR.sh); }
  for (let y = 158; y < base - 4; y++) for (let x = 271; x <= 281; x++) {
    if (y < 162 && Math.hypot(x - 276, y - 162) > 5.3) continue;
    put(x, y, x === 271 || x === 281 || (y < 162 && Math.hypot(x - 276, y - 162) > 4.3) ? TR.lt : x === 276 ? '#3a241c' : '#6a4228');
  }
  for (let y = 149; y < 155; y++) for (let x = 274; x <= 278; x++) put(x, y, y === 149 || x === 274 || x === 278 ? TR.lt : '#f2c070');   // the icon over the door
  for (let y = base - 4; y < base + 1; y++) for (let x = 263 - (y - base + 4); x <= 289 + (y - base + 4); x++) put(x, y, (y & 1) ? '#c8c0c8' : '#e8e4ec');
  for (let k = 0; k <= 9; k++) for (let x = 276 - 12 + k; x <= 276 + 12 - k; x++) put(x, EAVE - 1 - k, k === 0 ? TR.lt : x <= 276 - 11 + k ? TR.hi : x >= 276 + 11 - k ? TR.sh : CW[3]);
  for (const [i, j] of [[0, -1], [-1, 0], [1, 0], [0, 1], [0, 0]]) put(276 + i, EAVE - 5 + j, i || j ? '#e89a50' : '#ffd890');
  roofStrip(BODY.x0 - 1, 263, EAVE - 1); roofStrip(289, BELL.x - BELL.hw - 1, EAVE - 1);

  // the central drum, its harlequin dome rising behind three big kokoshniks, the lantern, the onion, the cross
  const mx = MAIN.x, mw = MAIN.hw;
  wall(mx - mw, mx + mw, 91, EAVE - 3, 0.04);
  for (let x = mx - mw; x <= mx + mw; x += 6) pilaster(x, 93, EAVE - 4);
  for (let x = mx - mw + 3; x < mx + mw - 1; x += 6) win(x, 99, 2, 11, 7);
  cornice(mx - mw - 1, mx + mw + 1, 90);
  for (let y = 69; y <= 86; y++) {
    const t = (86 - y) / 17, hw = Math.max(3, Math.round((mw - 1) * Math.sqrt(Math.max(0, 1 - Math.pow(t, 1.7)))));
    for (let x = mx - hw; x <= mx + hw; x++) put(x, y, harl(x, y, mx, x > mx + hw * 0.35, 4));
  }
  for (const k of [-13, 0, 13]) kokoshnik(mx + k, 90, 7, true);
  for (let y = 64; y < 69; y++) for (let x = mx - 3; x <= mx + 3; x++) put(x, y, y === 64 ? TR.hi : (x - mx) % 2 === 0 && y > 65 ? '#4a3a46' : x < mx + 2 ? TR.lt : TR.sh);
  onion(mx, 55, 63, 5);
  cross(mx, 43, 54);
  // the corner domes: drums on the main roof, clear of the central drum; blue-green checkered bulbs, tall gold onions
  for (const cx of CORNERS) {
    wall(cx - 10, cx + 9, 98, EAVE - 3, 0.06);
    pilaster(cx - 10, 100, EAVE - 4); pilaster(cx + 8, 100, EAVE - 4);
    win(cx - 6, 105, 3, 11, 8); win(cx + 3, 105, 3, 11, 9);
    cornice(cx - 11, cx + 10, 97);
    for (let y = 88; y <= 96; y++) {
      const t = (96 - y) / 8, hw = Math.max(2, Math.round(9 * Math.sqrt(Math.max(0, 1 - t * t * 0.9))));
      for (let x = cx - hw; x <= cx + hw; x++) put(x, y, y === 96 ? (x & 1 ? HARL.r : TR.lt) : harl(x, y, cx, x > cx + 3, 3, true));
    }
    for (let y = 84; y < 88; y++) for (let x = cx - 2; x <= cx + 2; x++) put(x, y, y === 84 ? TR.hi : x === cx ? HARL.r : TR.lt);
    onion(cx, 67, 83, 5);
    cross(cx, 55, 66);
  }
  // the bell tower: the belfry's arches over a balcony, the kokoshnik stage, the slim checkered tent with its lucarnes
  const bx = BELL.x, bw = BELL.hw;
  for (const ax of [bx - 8, bx + 3]) for (let y = 100; y < 116; y++) for (let x = ax; x < ax + 6; x++) {
    if (y < 103 && Math.hypot(x - ax - 2.5, y - 103) > 3.1) continue;
    put(x, y, x === ax ? TR.lt : y > 106 && y < 111 && (x === ax + 2 || x === ax + 3) ? '#a87a3a' : '#241c2c');
  }
  for (let x = bx - bw; x <= bx + bw; x++) { put(x, 116, TR.hi); put(x, 117, (x & 1) ? TR.sh : '#2a2232'); put(x, 118, TR.lt); }
  cornice(bx - bw - 1, bx + bw + 1, 95);
  wall(bx - 9, bx + 9, 89, 94, 0.06);
  for (const k of [-6, 0, 6]) kokoshnik(bx + k, 93, 2, false);
  for (let y = 61; y <= 88; y++) {
    const hw = Math.round(lerp(9, 2, (88 - y) / 27));
    for (let x = bx - hw; x <= bx + hw; x++) put(x, y, x === bx - hw ? TR.lt : x === bx + hw ? TR.sh : harl(x, y, bx, x > bx + 2));
  }
  for (const sx of [-1, 1]) {
    const lx = bx + sx * 4;
    for (let k = 0; k < 4; k++) for (let x = lx - 2 + (k >> 1); x <= lx + 2 - (k >> 1); x++) put(x, 82 - k, k === 3 ? TR.hi : TR.lt);
    put(lx, 81, '#3a2c3a'); put(lx, 80, '#3a2c3a');
  }
  for (let y = 57; y < 61; y++) for (let x = bx - 2; x <= bx + 2; x++) put(x, y, y === 57 ? TR.hi : (x & 1) ? HARL.r : TR.lt);
  onion(bx, 45, 56, 5);
  for (let y = 39; y < 45; y++) put(bx, y, GD.md);
  cross(bx, CA.tip, 38);
  return p.done();
}

/* ================= The square and the alley: snow, banks, the swept wet middle, the aryk, the lawns ================= */
// candelabra lamps, as in the park: a black post, three white globes; one row, off the path's line
const LAMPS = [{ x: 214, base: 186, h: 20 }, { x: 386, base: 188, h: 24 }, { x: 354, base: 197, h: 30 }, { x: 172, base: 222, h: 62 }];
const globes = L => {
  const r = Math.max(1, Math.round(L.h / 22)), top = L.base - L.h, arm = Math.round(L.h * 0.16), ay = top + Math.round(L.h * 0.14);
  return [[L.x, top - r, r], [L.x - arm, ay - r, r], [L.x + arm, ay - r, r]];
};
const BENCH = { x0: 330, x1: 358, y: 198 };
const SNOWMAN = { x: 420, base: 205 };
const SNOWG = ['#2e3860', '#3e4a76', '#55618c', '#6e7aa4', '#8a96bc', '#a8b2d2'];
const WARMG = ['#5a5068', '#866e72', '#b09280', '#d4b898', '#ecd8b8'];
const PATHG = ['#22263c', '#2a2e46', '#343852', '#40445e'];
const PACK = ['#3a4468', '#465276', '#56628a', '#6a76a0'];
const GLINTS = [];                                        // snow pixels that can sparkle with frost
const bankW = y => 2 + (y - PATH.y0) * 0.06;
function renderGround() {
  const p = painter(W, H), put = p.put;
  GLINTS.length = 0;
  const pool = (x, y) => LAMPS.reduce((a, L) => Math.max(a, 1 - Math.hypot((x - L.x) / (L.h * 1.2), (y - L.base - 2) / (L.h * 0.28))), 0);
  for (let y = HZ - 3; y < H; y++) for (let x = 0; x < W; x++) {
    const d = (y - HZ) / (H - HZ), n = hash(x, y, 141), dx = Math.abs(x - pathC(y)), pw = pathW(y);
    const inSquare = y < PATH.y0 + 2 && x > 226 && x < 370;
    const inPath = y >= PATH.y0 && dx < pw - bankW(y);
    const swept = inPath && dx < pw * 0.42 && hash(x >> 1, y, 145) > smooth(214, 244, y);
    const bank = y >= PATH.y0 && dx >= pw - bankW(y) && dx < pw + bankW(y);
    const apron = Math.hypot((x - 142) / 44, (y - 207) / 6) < 1;          // trodden snow in front of the kiosk
    const w = Math.max(0, pool(x, y)), cg = Math.max(0, 1 - Math.hypot((x - 300) / 120, (y - HZ) / 16));
    const kg = Math.max(0, 1 - Math.hypot((x - 96) / 70, (y - 210) / 14));  // the kiosk's light on the snow
    let col;
    if (inSquare) {
      const tile = ((x + (Math.floor(y / 2) & 1) * 2) % 4 === 0 || y % 2 === 0) ? -0.1 : 0;
      col = ramp(['#2a2c46', '#3e3a54', '#5e5262', '#86726e', '#a88c7a'], 0.25 + cg * 0.75 + tile + (n - 0.5) * 0.1, x, y);
    } else if (swept) {
      const tile = ((x + (Math.floor(y / 3) & 1) * 3) % 6 === 0 || y % 3 === 0) ? -0.1 : 0;
      col = ramp(PATHG, 0.5 + tile + (n - 0.5) * 0.16 + cg * 0.5, x, y);
      if (w > 0.2) col = mixHex(col, '#5a4a48', (w - 0.2) * 0.5);
    } else if (inPath || apron) {
      const print = hash(x >> 1, y >> 1, 146) < 0.06;
      col = ramp(PACK, 0.55 + d * 0.3 + (n - 0.5) * 0.14 + cg * 0.3 + kg * 0.3 - (print ? 0.25 : 0), x, y);
      if (w > 0.15) col = mixHex(col, '#c4a88c', (w - 0.15) * 0.6);
      if (kg > 0.2) col = mixHex(col, '#c49a78', (kg - 0.2) * 0.5);
    } else {
      let v = 0.36 + d * 0.3 + (n - 0.5) * 0.08 + cg * 0.4;
      if (bank) { const e = (dx - (pw - bankW(y))) / (2 * bankW(y)); v += e < 0.3 ? -0.2 : 0.18; }
      const warm = Math.max(w, kg * 0.8);
      col = warm > 0.12 ? ramp(WARMG, warm * 0.95 + v * 0.25, x, y) : ramp(SNOWG, v, x, y);
      if (!bank && y > 186 && !(x > 38 && x < 130 && y < 208)) GLINTS.push(y * W + x);
    }
    put(x, y, col);
  }
  // the aryk: the irrigation channel along the alley's left side, frozen, drifted over here and there
  for (let y = PATH.y0 + 3; y < H; y++) {
    const ax = Math.round(pathC(y) - pathW(y) - bankW(y) - 4), w = 1 + Math.floor((y - PATH.y0) / 40);
    if (hash(ax, y >> 2, 147) < 0.2) continue;
    for (let i = 0; i < w + 1; i++) put(ax + i, y, i === w ? '#4e5a82' : hash(ax, y, 142) < 0.3 ? '#6a7aa4' : '#222a46');
  }
  // clipped box hedges under snow
  for (const [x0, x1, y0] of [[134, 172, 189], [396, 446, 192]]) for (let x = x0; x <= x1; x++) {
    const h = Math.round(5 + Math.sin(Math.PI * (x - x0) / (x1 - x0)) * 1.5);
    for (let y = y0 - h; y <= y0; y++) put(x, y, y < y0 - h + 2 ? (hash(x, y, 143) < 0.7 ? '#c2cae4' : '#9aa4c8') : y < y0 - 1 ? (hash(x, y, 144) < 0.3 ? '#3a5a4a' : '#1e3228') : '#141828');
  }
  // footprints across the lawn to the bench and the snowman
  for (const [xa, ya, xb, yb] of [[268, 201, BENCH.x0 + 4, BENCH.y + 6], [BENCH.x1, BENCH.y + 6, SNOWMAN.x - 8, SNOWMAN.base]]) for (let k = 0; k < 12; k++) {
    const u = k / 11, x = Math.round(lerp(xa, xb, u)), y = Math.round(lerp(ya, yb, u) + (k & 1));
    put(x, y, '#3a4470'); put(x + 1, y, '#46527c');
  }
  // the bench: wooden slats on cast-iron legs, swept; the snowman with his carrot, coal buttons and twig arms. Both on
  // canvases of their own, drawn in feet order with the people
  GROUND_C = p.done();
  const pb = painter(W, H), ps = painter(W, H);
  { const put = pb.put, { x0, x1, y } = BENCH;
  for (let x = x0; x <= x1; x++) { put(x, y - 6, '#6a4a34'); put(x, y - 5, '#4a3424'); put(x, y, '#8a6444'); put(x, y + 1, '#5a3e2a'); }
  for (const lx of [x0 + 1, x1 - 1]) for (let j = y - 7; j <= y + 6; j++) put(lx, j, '#1a1a24');
  for (let x = x0; x <= x1; x++) put(x, y + 7, '#2a3052'); }
  const put2 = ps.put, { x: sx, base: sb } = SNOWMAN;
  for (const [cy, r] of [[sb - 4, 4.6], [sb - 11, 3.4], [sb - 16, 2.4]]) for (let j = -5; j <= 5; j++) for (let i = -5; i <= 5; i++) {
    const dd = Math.hypot(i, j);
    if (dd <= r) put2(sx + i, cy + j, i + j < -2 ? '#f0f4ff' : i + j < 2 ? '#d0d8f0' : '#9ea8cc');
  }
  for (const [i, j, c] of [[0, -11, '#1a1a22'], [0, -9, '#1a1a22'], [-1, -17, '#1a1a22'], [1, -17, '#1a1a22'], [1, -16, '#e8762a'], [2, -16, '#e8762a']]) put2(sx + i, sb + j, c);
  for (const s of [-1, 1]) bres(put2, sx + s * 3, sb - 12, sx + s * 7, sb - 15, '#4a3424');
  for (let i = -2; i <= 2; i++) put2(sx + i, sb - 19, '#c83a3a');             // a red knitted hat
  put2(sx - 1, sb - 20, '#c83a3a'); put2(sx, sb - 20, '#c83a3a'); put2(sx, sb - 21, '#f0e8e0');
  for (let i = -4; i <= 5; i++) put2(sx + i, sb + 1, '#3e4a76');
  BENCH_C = pb.done(); SNOWMAN_C = ps.done();
  return GROUND_C;
}
function renderLamps() {
  const p = painter(W, H), put = p.put;
  for (const L of LAMPS) {
    const top = L.base - L.h, pw = L.h > 40 ? 2 : 1;
    for (let y = top; y <= L.base; y++) for (let i = 0; i < pw; i++) put(L.x + i - (pw >> 1), y, i ? '#0e0e16' : '#2a2a38');
    const bw = Math.max(1, Math.round(L.h / 16));
    for (let y = L.base - Math.round(L.h * 0.1); y <= L.base; y++) for (let i = -bw; i <= bw; i++) put(L.x + i, y, i < 0 ? '#2e2e3e' : '#14141c');
    const [, [lx, ly], [rx]] = globes(L);
    for (let x = lx; x <= rx; x++) put(x, ly + Math.max(1, Math.round(L.h / 22)) + 1, '#1e1e2a');
    for (const [gx, gy, r] of globes(L)) for (let y = -r; y <= r; y++) for (let x = -r; x <= r; x++) {
      if (Math.hypot(x, y) > r + 0.4) continue;
      put(gx + x, gy + y, x + y < 0 ? '#fffcf0' : '#ffe8bc');
    }
  }
  return p.done();
}

/* ================= The Soviet block beyond the park, and its roof sign: the billboard ================= */
function renderBlock() {
  const p = painter(W, H), put = p.put, { x0, roof } = BLOCK;
  for (let y = roof; y < HZ + 6; y++) for (let x = x0; x < W; x++) {
    const panel = (x - x0) % 8 === 0 || (y - roof) % 6 === 0;
    put(x, y, y === roof ? '#5a5a74' : x === x0 ? '#46465e' : panel ? '#24243a' : '#30304a');
  }
  for (let y = roof + 2; y < HZ; y += 6) for (let x = x0 + 3; x < W - 2; x += 8) {
    const n = hash(x, y, 151);
    for (let j = 0; j < 3; j++) for (let i = 0; i < 3; i++) put(x + i, y + j, n < 0.45 ? (j === 0 ? '#ffe2a6' : '#f2b866') : n < 0.5 ? '#9ab0e0' : '#1c1c2c');
    put(x - 1, y + 3, '#4a4a62'); put(x + 3, y + 3, '#4a4a62');
  }
  for (let x = x0; x < W; x++) put(x, roof - 1, hash(x, 152) < 0.8 ? '#c8d0ea' : '#9aa6c8');
  return p.done();
}
const BB_LAMPS = [0.18, 0.5, 0.82].map(f => BB.x + Math.round((BB.w - 1) * f));
function renderBillboard() {                              // a steel frame on the block's roof, a catwalk, three lamps
  const p = painter(W, H), put = p.put, X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const cx of [BB.x + 6, BB.x + BB.w - 7]) for (let y = Y1 + 1; y < BLOCK.roof - 1; y++) { put(cx, y, '#3a3a4c'); put(cx + 1, y, '#16161e'); }
  for (let x = X0 - 2; x <= X1 + 2; x++) { put(x, Y1 + 1, '#4a4a5c'); put(x, Y1 + 2, (x & 1) ? '#22222c' : '#16161e'); }
  for (let x = X0; x <= X1; x++) { put(x, Y0, '#14141c'); put(x, Y1, '#14141c'); put(x, Y0 - 1, '#c8d0ea'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#14141c'); put(X1, y, '#14141c'); }
  for (const lx of BB_LAMPS) { for (let y = Y0 - 4; y < Y0; y++) put(lx, y, '#3a3a48'); for (let i = lx - 2; i <= lx + 1; i++) put(i, Y0 - 5, '#2a2a36'); }
  return p.done();
}
// the city's own ad: АПОРТ ШЫРЫН, juice of the Aport, the big red apple Almaty ("of apples") is famous for
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) put(x, y, ramp(['#2a6a3e', '#1f5634', '#17442a', '#103420'], y / h * 0.8 + Math.abs(x - w / 2) / w * 0.3, x, y));
  for (let x = 1; x < w - 1; x++) { put(x, 1, '#d8b860'); put(x, h - 2, '#a88a40'); }
  for (let y = 1; y < h - 1; y++) { put(1, y, '#d8b860'); put(w - 2, y, '#a88a40'); }
  const ax = 46, ay = 10;
  for (let y = -5; y <= 5; y++) for (let x = -5; x <= 5; x++) {
    const d = Math.hypot(x * 1.05, y * 1.1 - (Math.abs(x) < 2 && y < -2 ? 1.2 : 0));
    if (d > 3.9) continue;
    put(ax + x, ay + y, x + y < -5 ? '#ff8a7a' : x + y < 1 ? '#e83a3a' : x + y < 5 ? '#b82230' : '#7e1824');
  }
  put(ax - 2, ay - 2, '#ffd0c0'); put(ax, ay - 5, '#5a3a20'); put(ax, ay - 6, '#5a3a20');
  put(ax + 1, ay - 6, '#6ad06a'); put(ax + 2, ay - 7, '#6ad06a'); put(ax + 2, ay - 6, '#3a9a4a');
  const c = p.done();
  const [g, gx] = makeCanvas(w, h);
  sign(gx, 'АПОРТ', 0, 0, '#fff2d6');
  p.x.imageSmoothingEnabled = false;
  p.x.drawImage(g, 0, 0, 20, 5, 3, 3, 40, 10);
  sign(p.x, 'ШЫРЫН', 3 + ((40 - signWidth('ШЫРЫН')) >> 1), 14, '#f2c860');
  return gradeAd(c, { level: 0.86, tint: '#141a3a', mix: 0.08, wear: 0.14, seed: 57 });
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 8, 0.7);
  blit(BB_C);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 7, 2, 1, '#f2e2c0'); halo(HL.bbLamp, lx, BB.y - 6, 0.85); }
}

/* ================= The tea kiosk: the cozy corner ================= */
// a timber kiosk on the snow by the alley: snow thick on its roof, a string of bulbs, ШАЙ · КОФЕ over a lit window with
// the urn by the hatch, cups on a shelf, a crate of red apples, the menu card; АЛМА chalked on a board; a stovepipe
const KW = { x0: KIOSK.x0 + 5, x1: KIOSK.x1 - 5, y0: KIOSK.top + 19, y1: KIOSK.top + 33 };   // the window
const URN = { x: KIOSK.x1 - 21 };
function renderKiosk() {
  const p = painter(W, H), put = p.put, { x0, x1, top, base } = KIOSK;
  for (let k = 0; k < 7; k++) for (let x = x0 - 6 + k; x <= x1 + 6 - k; x++) put(x, top + 6 - k, k === 0 ? '#2a1e1e' : k < 3 ? '#5a3428' : '#6a3e30');
  for (let k = 0; k < 4; k++) for (let x = x0 - 4 + k * 2; x <= x1 + 4 - k * 2; x++) if (hash(x, k, 161) < 0.92 || k < 2) put(x, top - k, k === 0 ? '#c4cce6' : k === 3 ? '#9aa4c8' : '#e2e8f8');
  for (let y = top + 7; y < base; y++) for (let x = x0; x <= x1; x++) put(x, y, x === x0 ? '#8a5a3a' : x === x1 ? '#3a2418' : (y - top) % 3 === 0 ? '#4a2e20' : '#6e4630');
  for (let y = top + 9; y < top + 18; y++) for (let x = x0 + 3; x <= x1 - 3; x++) put(x, y, y === top + 9 || y === top + 17 ? '#c8a060' : '#5a1e22');   // the fascia
  for (let y = KW.y0; y <= KW.y1; y++) for (let x = KW.x0; x <= KW.x1; x++) {
    const v = (y - KW.y0) / (KW.y1 - KW.y0) + (bayer(x, y) - 0.5) * 0.25;
    put(x, y, x === KW.x0 || x === KW.x1 || y === KW.y0 ? '#3a2418' : v < 0.35 ? '#ffe2a6' : v < 0.75 ? '#f6c070' : '#e09a50');
  }
  for (let y = KW.y0 + 3; y < KW.y1 - 1; y++) for (let x = URN.x; x <= URN.x + 5; x++) put(x, y, y === KW.y0 + 3 ? '#e8e8f0' : x === URN.x ? '#f0f0f8' : x === URN.x + 5 ? '#7a7a88' : '#b4b4c4');   // the urn
  put(URN.x - 1, KW.y0 + 9, '#7a7a88'); put(URN.x + 2, KW.y0 + 2, '#5a5a66');
  for (let x = x0 + 26; x < x0 + 46; x += 3) { put(x, KW.y0 + 4, '#f8f4ec'); put(x + 1, KW.y0 + 4, '#d8d0c4'); }
  for (let x = x0 + 25; x < x0 + 47; x++) put(x, KW.y0 + 5, '#6a4430');
  for (let y = KW.y0 + 2; y < KW.y0 + 9; y++) for (let x = x0 + 9; x < x0 + 19; x++) put(x, y, y === KW.y0 + 2 ? '#3a2418' : (y & 1) ? '#f2ead8' : '#c8bca4');   // the menu card
  for (let x = x0 + 2; x <= x1 - 2; x++) { put(x, KW.y1 + 1, '#c89a62'); put(x, KW.y1 + 2, '#6a4a30'); }        // the counter
  for (let y = KW.y1 - 4; y <= KW.y1; y++) for (let x = x0 + 7; x < x0 + 22; x++) {                           // the crate of apples
    const apple = y < KW.y1 - 1 && hash(x, y, 162) < 0.85;
    put(x, y, apple ? (hash(x, y, 163) < 0.3 ? '#ff6a5a' : hash(x, y, 164) < 0.5 ? '#d82a32' : '#a81c28') : '#8a6038');
  }
  for (let y = top + 40; y < top + 48; y++) for (let x = x0 + 4; x <= x0 + 23; x++) put(x, y, x === x0 + 4 || x === x0 + 23 || y === top + 40 || y === top + 47 ? '#8a6038' : '#1e2420');   // the board
  for (let y = top - 16; y < top + 1; y++) { put(x1 - 10, y, '#3a3a44'); put(x1 - 9, y, '#1e1e26'); }
  put(x1 - 11, top - 16, '#4a4a54'); put(x1 - 8, top - 16, '#2a2a32');
  for (let x = x0 - 2; x <= x1 + 2; x++) put(x, base, '#141828');
  const c = p.done();
  sign(p.x, 'ШАЙ · КОФЕ', ((x0 + x1 - signWidth('ШАЙ · КОФЕ')) >> 1) + 1, top + 11, '#f6e6c0');
  sign(p.x, 'АЛМА', x0 + 7, top + 42, '#e8ece4');
  return c;
}
const KIOSK_BULBS = [];
for (let x = KIOSK.x0 - 4; x <= KIOSK.x1 + 4; x += 4) KIOSK_BULBS.push(x);

/* ================= People: wrapped up for a frosty night ================= */
const SKINS = ['#f2c8a0', '#e0aa80', '#c08a60', '#8a5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#c8a058', '#8e3e1c', '#c8c4c0'];
const PNIGHT = '#141a3a', GLOW = '#ffdcae';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.2, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * 0.4);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 17 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOW, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, GLOW, 0.3), bot: m(bot), shoe: m('#1e1a20'), long: o.long || 0,
    hat: o.hat ? m(o.hat) : null, apron: o.apron ? m(o.apron) : null, scarf: o.scarf ? m(o.scarf) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 phone up, 3 out (offering, handing over, holding a lead or a rope), 5 a cup at the mouth;
 * o.lean: the head leant a pixel (a couple)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit && view === 1) {
    R(tx, fy + 1, 2, 4, P.bot); R(tx + bw - 2, fy + 1, 2, 4, P.bot); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
  } else if (o.sit && side) {
    const tl = P.h >= 20 ? 6 : 4, sh = o.shin ?? 3;
    R(dir > 0 ? x - 1 : x - tl + 2, fy - 1, tl, 2, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 2, fy + 1, 2, sh, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 1, fy + 1 + sh, 3, 1, P.shoe);
  }
  R(tx, ty, bw, th, P.top);                                  // a winter coat
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                  // the lamps' light on the shoulders
  if (P.apron && view !== 0) R(side ? (dir > 0 ? x : x - 1) : tx + 1, ty + 2, side ? 2 : bw - 2, th - 1, P.apron);
  if (P.scarf) R(tx, ty, bw, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); R(p0, view === 0 ? hy - 2 : hy - 1, 2, 1, view === 0 ? '#ffe2a8' : '#8ad8e8'); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); R(ax + (sg < 0 ? 1 : -1), hy + hh - 1, 2, 2, '#f2ece0'); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 5) { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, hy + hh - 1, 2, 2, '#f2ece0'); }
    else if (st === 2) { R(x + dir, ty - 2, 1, 4, P.topD); R(x + dir * 2 - (dir < 0 ? 1 : 0), hy - 1, 2, 3, '#0e1018'); R(x + dir * 2 - (dir < 0 ? 1 : 0), hy, 2, 1, '#8ad8e8'); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) { R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL); if (P.long) R(hx, hy + hh, hw, P.long, P.hair); }
  else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx, hy - 1, hw, 1, mixHex(P.hat, GLOW, 0.3)); }   // a knitted hat
  return hy;
}
const TOPS = ['#8a2a34', '#2a4a7a', '#3a6a4a', '#6a4a8a', '#c87a3a', '#2a2a3a', '#7a6a5a', '#b84a5a', '#4a6a9a', '#d8c8a8'];
const BOTS = ['#22202c', '#33466e', '#3a3a48', '#4a3a30'];
const HATS = ['#d84a4a', '#e8e0d0', '#3a5aa0', '#e0b040', '#2a2a34', '#3a9a7a'];
const rk = i => frac((i + 0.5) * 0.618);
const sizeAt = y => Math.round(lerp(13, 20, clamp((y - 184) / 22, 0, 1)));

/* ================= The loop's timeline (seconds) =================
   all loop   gondola cabins glide up and down Kok-Tobe, the wheel turns; cloud shadows drift over the range, mist over
              the foothills, wisps across the sky; windows flicker; frost glints on the snow; steam and stove smoke
   every 24 s the kiosk serves a cup (6, 30, 54 …): the vendor fills it at the urn and hands it out of the hatch, the
              customer walks off up the alley with it, the queue shuffles up and someone new walks in from the right
   every 4 s  the sweeper throws a shovelful of snow off the path; every 30 s the woman on the bench leans to him
   14-54      a parent pulls a child on a sled across the lawn, right to left; back 74-114, again 134-174 and 194-234
   24-58      the TV tower's light show: colour climbs the shaft (chime at 24); the man on the bench films it; again 144-178
   30-70      a dog walker crosses the square's edge, left to right; back 150-190
   64-86      a plane on its approach to the airport crosses the sky, right to left; again 200-222
   92-120     evening bells: the belfry lights up (92), eight strokes 98-112, the rooks lift off the roofs and the trees
              and circle the square, settling back by 136
   186-226    the snow thickens and thins out again (the city's own snow)                                              */
const QP = 24, QT0 = 6;
const SHOWS = [[24, 58], [144, 178]];
const SLED = [{ t0: 14, t1: 54, dir: -1 }, { t0: 74, t1: 114, dir: 1 }, { t0: 134, t1: 174, dir: -1 }, { t0: 194, t1: 234, dir: 1 }];
const DOGW = [{ t0: 30, t1: 70, dir: 1 }, { t0: 150, t1: 190, dir: -1 }];
const PLANES = [[64, 86], [200, 222]];
const BELLS = [98, 100, 102, 104, 106, 108, 110, 112];
const BELFRY = [92, 120], FLOCK = [98, 136], SNOWBURST = [186, 226];
const showAt = t => SHOWS.reduce((a, [s, e]) => Math.max(a, env(t, s, e, 3)), 0);
const bellGlow = t => BELLS.reduce((a, b) => Math.max(a, t >= b && t < b + 1.8 ? 1 - (t - b) / 1.8 : 0), 0);

/* ----- the kiosk's queue: a little machine serving a cup every 24 s ----- */
const QX = [128, 140, 152];
const CUST = Array.from({ length: 10 }, (_, i) => ({
  h: 19 + (i % 3 === 0 ? 1 : 0), args: [i % 4, (i * 3) % 6, TOPS[(i * 7) % 10], BOTS[i % 4], { hat: i % 4 === 3 ? null : HATS[(i * 5) % 6], scarf: i % 3 === 1 ? '#c83a3a' : null, long: i % 5 === 2 ? 3 : 0 }],
  rank: [0.05, 0.12, 0.62, 0.2, 0.28, 0.75, 0.33, 0.4, 0.86, 0.46][i], cache: {},
}));
const custLook = (c, h = c.h) => c.cache[h] || (c.cache[h] = look(h, ...c.args));
const EXIT = [[QX[0], 206], [214, 205], [248, 194], [270, 186], [300, 184], [496, 184]];
function along(pts, u) {                                // a point at share u along a polyline
  let total = 0; const seg = [];
  for (let i = 1; i < pts.length; i++) { const l = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]); seg.push(l); total += l; }
  let d = clamp(u, 0, 1) * total;
  for (let i = 0; i < seg.length; i++) { if (d <= seg[i]) { const f = d / seg[i]; return [lerp(pts[i][0], pts[i + 1][0], f), lerp(pts[i][1], pts[i + 1][1], f), Math.sign(pts[i + 1][0] - pts[i][0]) || 1]; } d -= seg[i]; }
  const e = pts[pts.length - 1]; return [e[0], e[1], 1];
}
function queue(t, crowd, put) {
  const s = ((t - QT0) % LOOP + LOOP) % LOOP, k = Math.floor(s / QP), f = s - k * QP, stride = step(t, 768) & 1;
  const cust = n => CUST[((n % 10) + 10) % 10];
  // the one just served walks off up the alley with the cup, steam rising from it
  if (f < 22 && cust(k).rank < crowd) {
    const [x, y, dir] = along(EXIT, f / 22), c = cust(k), Q = custLook(c, Math.min(c.h, sizeAt(y)));
    put(y, () => {
      const hy = person(x, y, Q, { view: 2, dir, step: stride, aR: 5 });
      steam(x + dir * 2, hy + 1, t, k);
    });
  }
  // the queue shuffles up, the next is served: the vendor fills a cup at the urn and hands it out of the hatch
  const at = (n, from, to, a, b) => lerp(QX[from], QX[to], smooth(a, b, f));
  for (const [n, x] of [[k + 1, at(0, 1, 0, 0.5, 2)], [k + 2, at(0, 2, 1, 1, 2.5)]]) {
    const c = cust(n);
    if (c.rank >= crowd) continue;
    const moving = (n === k + 1 && f > 0.5 && f < 2) || (n === k + 2 && f > 1 && f < 2.5), serving = n === k + 1 && f > QP - 3;
    put(206 - (n - k - 1), () => person(x, 206 - (n - k - 1), custLook(c), { view: 2, dir: -1, step: moving ? stride : 0, aR: serving ? 3 : 0 }));
  }
  // someone new walks in from the right along the near row and joins the end
  const c3 = cust(k + 3);
  if (c3.rank < crowd && f > 2) {
    const u = smooth(2, 22, f) * 0.9 + clamp((f - 2) / 20, 0, 1) * 0.1, x = lerp(492, QX[2], u);
    put(204, () => person(x, 204, custLook(c3), { view: 2, dir: -1, step: u < 1 ? stride : 0 }));
  }
}
function steam(x, y, t, seed) {
  for (let j = 0; j < 3; j++) {
    const u = frac(t / 2 + j / 3 + seed * 0.13);              // 120 puffs a loop
    ctx.globalAlpha = 0.4 * (1 - u);
    R(Math.round(x + Math.sin(u * 6 + j + seed) * 1.2), Math.round(y - u * 7), 1, 1, '#e8eef8');
  }
  ctx.globalAlpha = 1;
}
// the vendor in the window: waits, turns to the urn, fills a cup, hands it out at the hatch
const VENDOR = look(20, 0, 0, '#a83a3a', BOTS[0], { hat: '#e8e0d0', apron: '#e8e0d0', dk: 0.05 });
function drawVendor(t) {
  const s = ((t - QT0) % LOOP + LOOP) % LOOP, f = s - Math.floor(s / QP) * QP;
  const toUrn = smooth(QP - 10, QP - 8, f) * (1 - smooth(QP - 6, QP - 5, f)), toHatch = smooth(QP - 6, QP - 4, f) * (1 - smooth(QP - 0.6, QP, f));
  const x = Math.round(lerp(88, URN.x - 3, toUrn) + (KW.x1 - 6 - 88) * toHatch);
  ctx.save(); ctx.beginPath(); ctx.rect(KW.x0 + 1, KW.y0 + 1, KW.x1 - KW.x0 - 1, KW.y1 - KW.y0); ctx.clip();
  const hy = person(x, KW.y1 + 9, VENDOR, { view: toHatch > 0.5 || toUrn > 0.5 ? 2 : 1, dir: 1, aR: toHatch > 0.8 || toUrn > 0.8 ? 3 : 0 });
  ctx.restore();
  if (toUrn > 0.8) steam(URN.x + 2, KW.y0 + 6, t, 3);
  if (toHatch > 0.9) R(KW.x1 - 1, hy + 7, 2, 2, '#f2ece0');
}

/* ----- the people who stay, and the passers-by ----- */
const BENCH_PAIR = [{ x: 338, P: look(19, 0, 3, TOPS[3], BOTS[1], { hat: HATS[3], long: 2 }) }, { x: 349, P: look(20, 1, 1, TOPS[5], BOTS[2], { hat: HATS[4], scarf: '#4a8ac8' }) }];
const SWEEPER = { x: 279, y: 204, P: look(20, 2, 0, '#e8762a', BOTS[2], { hat: HATS[4] }), rank: rk(3) };
const PHOTO = { x: 252, y: 196, P: look(17, 1, 2, TOPS[1], BOTS[0], { hat: HATS[5], scarf: '#e8c040' }), rank: rk(19) };
const PORCH = [{ x: 262, y: 181, P: look(13, 1, 0, TOPS[5], BOTS[0], { dk: 0.3, hat: HATS[1] }), rank: rk(0) }, { x: 291, y: 181, P: look(13, 2, 1, TOPS[9], BOTS[2], { dk: 0.3, long: 1 }), rank: rk(16) }];
const STROLL = [{ k: 2, dir: 1, y: 184, ph: 0.1, P: look(13, 0, 2, TOPS[0], BOTS[1], { dk: 0.3, hat: HATS[2] }), rank: rk(13) },
  { k: 3, dir: -1, y: 182, ph: 0.55, P: look(13, 3, 0, TOPS[6], BOTS[0], { dk: 0.3, long: 2 }), rank: rk(14) }];
const PARENT = look(19, 0, 1, TOPS[8], BOTS[0], { hat: HATS[1] }), KID = look(12, 0, 3, TOPS[7], BOTS[1], { hat: HATS[0] });
const DOGMAN = look(17, 1, 2, TOPS[6], BOTS[0], { hat: HATS[2], scarf: '#e8c040' });
const DOG = sprite(['.......kk', 'w.....www', '.wwwwwwg.', '.wwwwww..', '.w.g.w.g.'], { w: '#9a6a44', g: '#5a3a26', k: '#e8d8c4' });
const DOG_L = sprite(['.......kk', 'w.....www', '.wwwwwwg.', '.wwwwww..', '.w.g.w.g.'], { w: '#9a6a44', g: '#5a3a26', k: '#e8d8c4' }, true);
function people(t, crowd, put) {
  const stride = step(t, 768) & 1;
  queue(t, crowd, put);
  if (rk(11) < crowd) {                                    // the couple on the bench: she leans to him, he films the shows
    const lean = wave(t, 8, 0.1) > 0.55 ? 1 : 0, film = showAt(t) > 0.3;
    put(BENCH.y + 0.5, () => { person(BENCH_PAIR[0].x, BENCH.y, BENCH_PAIR[0].P, { view: 1, sit: true, lean }); person(BENCH_PAIR[1].x, BENCH.y, BENCH_PAIR[1].P, { view: 1, sit: true, aL: film ? 2 : 0 }); });
  }
  if (SWEEPER.rank < crowd) {                              // the sweeper clearing the path's edge: scoop, lift, throw
    const u = frac(t / 4), lift = u > 0.4 && u < 0.75, { x, y, P } = SWEEPER;
    put(y, () => {
      person(x, y, P, { view: 2, dir: 1, aR: 3 });
      ctx.fillStyle = '#8a6a44';
      const bx = lift ? x + 6 : x - 6, by = lift ? y - 9 : y - 1;
      for (let k = 0; k <= 6; k++) ctx.fillRect(Math.round(lerp(x + 3, bx, k / 6)), Math.round(lerp(y - 9, by, k / 6)), 1, 1);
      R(bx - 1, by, 3, 2, '#5a5e6a');
      if (!lift && u < 0.4) R(bx - 1, by - 1, 3, 1, '#d6def2');
      if (u >= 0.6 && u < 0.95) { const v = (u - 0.6) / 0.35; R(Math.round(x + 7 + v * 12), Math.round(y - 10 - Math.sin(Math.PI * v) * 6 + v * 8), 2, 2, '#e2e8f8'); }
    });
  }
  if (PHOTO.rank < crowd) put(PHOTO.y, () => person(PHOTO.x, PHOTO.y, PHOTO.P, { view: 0, aR: wave(t, 12, 0.3) > -0.4 ? 2 : 0 }));
  for (const c of PORCH) if (c.rank < crowd) put(c.y, () => person(c.x, c.y, c.P, { view: step(t, 12) & 1 ? 0 : 1 }));
  for (const s of STROLL) if (s.rank < crowd) {
    const u = frac(t * s.k / LOOP + s.ph), x = s.dir > 0 ? lerp(-20, 500, u) : lerp(500, -20, u);
    put(s.y, () => person(x, s.y, s.P, { view: 2, dir: s.dir, step: stride }));
  }
  const sl = SLED.find(s => t >= s.t0 && t < s.t1);       // the sled across the lawn, the child riding, the rope taut
  if (sl && rk(5) < crowd) {
    const u = (t - sl.t0) / (sl.t1 - sl.t0), x = sl.dir < 0 ? lerp(500, -40, u) : lerp(-40, 500, u), y = 199, sx = x - sl.dir * 16;
    put(y, () => {
      R(sx - 5, y - 2, 10, 2, '#b8323a'); R(sx - 6, y, 12, 1, '#3a3036');
      person(sx, y - 3, KID, { view: 2, dir: sl.dir, sit: true, shin: 2 });
      ctx.fillStyle = '#5a4a40';
      for (let k = 0; k <= 8; k++) ctx.fillRect(Math.round(lerp(sx + sl.dir * 5, x - sl.dir, k / 8)), Math.round(lerp(y - 2, y - 8, k / 8)), 1, 1);
      person(x, y, PARENT, { view: 2, dir: sl.dir, step: stride, aR: 0 });
    });
  }
  const dw = DOGW.find(s => t >= s.t0 && t < s.t1);       // the dog walker along the square's edge
  if (dw && rk(8) < crowd) {
    const u = (t - dw.t0) / (dw.t1 - dw.t0), x = dw.dir > 0 ? lerp(-20, 500, u) : lerp(500, -20, u), y = 190;
    put(y, () => {
      person(x, y, DOGMAN, { view: 2, dir: dw.dir, step: stride, aR: 3 });
      const dx = Math.round(x + dw.dir * 12);
      ctx.drawImage(dw.dir > 0 ? DOG : DOG_L, dx - 4, y - 4 - (stride && (step(t, 1536) & 1) ? 1 : 0));
      ctx.fillStyle = '#3a3036';
      for (let k = 0; k <= 6; k++) ctx.fillRect(Math.round(lerp(x + dw.dir * 4, dx - dw.dir * 3, k / 6)), Math.round(lerp(y - 10, y - 4, k / 6)), 1, 1);
    });
  }
}

/* ----- the rooks: roosting on the cathedral's roofs and in the trees, lifting off at the bells ----- */
const FLOCK_BIRDS = [[238, 120], [250, 120], [258, 119], [296, 120], [312, 120], [318, 119], [158, 146], [170, 142], [182, 150], [196, 140], [146, 152], [204, 148]]
  .map(([x, y], i) => ({ x, y, r: 46 + (i % 4) * 9, ph: i * 0.53, sp: 1 + (i % 3) * 0.15 }));
function drawRooks(t) {
  const flap = step(t, 1920) & 1;
  ctx.fillStyle = '#16141e';
  for (const [x, y] of ROOKS) { R(x, y, 3, 2, '#16141e'); R((step(t, 48) + x) % 3 === 0 ? x + 2 : x, y - 1, 1, 1, '#16141e'); }
  for (const b of FLOCK_BIRDS) {
    let x = b.x, y = b.y, fly = false;
    if (t >= FLOCK[0] && t < FLOCK[1]) {
      const u = (t - FLOCK[0]) / (FLOCK[1] - FLOCK[0]), m = smooth(0, 0.1, u) * (1 - smooth(0.82, 1, u)), a = TAU * (u * 1.3 * b.sp) + b.ph;
      x = lerp(b.x, 290 + Math.cos(a) * b.r, m); y = lerp(b.y, 74 + Math.sin(a) * b.r * 0.32, m); fly = m > 0.05;
    }
    x = Math.round(x); y = Math.round(y);
    if (fly) { R(x - 1, y, 3, 1, '#16141e'); R(x - 2, y - (flap ? 1 : -1), 1, 1, '#16141e'); R(x + 2, y - (flap ? 1 : -1), 1, 1, '#16141e'); }
    else { R(x, y - 2, 2, 2, '#16141e'); R(x + 2, y - 2, 1, 1, '#2a2432'); }
  }
}

/* ----- the sky's life: the plane on its approach, the TV tower's show, the gondola and the wheel ----- */
function drawPlane(t) {
  const w = PLANES.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const u = (t - w[0]) / (w[1] - w[0]), x = Math.round(lerp(500, -24, u)), y = Math.round(lerp(44, 64, u));
  R(x, y, 6, 1, '#2a2e3e'); R(x + 2, y - 1, 2, 1, '#2a2e3e');
  R(x - 1, y, 1, 1, '#fff6e0'); halo(HL.landing, x - 1, y, 0.7);
  if (wave(t, 160) > 0.3) { R(x + 3, y + 1, 1, 1, '#ff5a4a'); halo(HL.tvTop, x + 3, y + 1, 0.6); }
}
const SHOW_COLS = ['#4a8aff', '#3ad0c8', '#b86aff', '#ffcf5a'];
function drawTowerShow(t, a) {
  if (a <= 0) return;
  const { x, top, foot } = TV, sh = Math.floor(t * 6);
  ctx.globalAlpha = a;
  for (let y = top; y <= foot; y++) {
    const i = ((Math.floor((foot - y) / 7) - sh) % 4 + 4) % 4;
    ctx.fillStyle = SHOW_COLS[i];
    ctx.fillRect(y < tvAt(0.68) ? x - (y > top + 10 ? 1 : 0) : x - 1, y, y < tvAt(0.68) ? (y > top + 10 ? 2 : 1) : 3, 1);
  }
  ctx.globalAlpha = 1;
  for (let k = 0; k < 3; k++) { const y = Math.round(lerp(foot - 8, top + 12, k / 2)), i = ((Math.floor((foot - y) / 7) - sh) % 4 + 4) % 4; halo(HL.show[i], x, y, a * 0.9); }
}
const GONDOLA = { x0: 226, y0: 140, x1: 84, y1: 107 };
const gondolaY = (x, off) => lerp(GONDOLA.y0, GONDOLA.y1, (GONDOLA.x0 - x) / (GONDOLA.x0 - GONDOLA.x1)) + off + Math.sin(Math.PI * (GONDOLA.x0 - x) / (GONDOLA.x0 - GONDOLA.x1)) * 3;
function drawKokTobe(t) {
  ctx.fillStyle = '#5a5470';
  for (let x = GONDOLA.x1; x <= GONDOLA.x0; x++) { ctx.fillRect(x, Math.round(gondolaY(x, 0)), 1, 1); if (x & 1) ctx.fillRect(x, Math.round(gondolaY(x, 2)), 1, 1); }
  for (let k = 0; k < 6; k++) for (const [dir, off] of [[1, 0], [-1, 2]]) {
    const u = frac(t * 8 / LOOP * dir + k / 6 + (dir < 0 ? 0.08 : 0)), x = Math.round(lerp(GONDOLA.x0, GONDOLA.x1, u)), y = Math.round(gondolaY(x, off));
    R(x, y + 1, 2, 2, '#ffd890'); R(x, y + 1, 2, 1, '#fff0c8'); halo(HL.cabin, x + 1, y + 2, 0.6);
  }
  for (let x = 6, i = 0; x < 150; x += 4, i++) {
    const y = Math.round(kokY(x)) - 1, a = 0.7 + 0.3 * wave(t, 24 + (i % 5) * 6, i * 0.17);
    R(x, y, 1, 1, i % 4 ? '#ffd890' : '#ff9ad0'); halo(HL.crest, x, y, a * 0.7);
  }
  const { x: wx, y: wy, r } = WHEEL, base = Math.round(kokY(wx));
  ctx.fillStyle = '#3a3450';
  for (let y = wy; y < base; y++) { ctx.fillRect(wx - 1 - Math.round((y - wy) * 0.3), y, 1, 1); ctx.fillRect(wx + 1 + Math.round((y - wy) * 0.3), y, 1, 1); }
  for (let k = 0; k < 14; k++) {
    const a = TAU * (k / 14 + t * 2 / LOOP), x = Math.round(wx + Math.cos(a) * r), y = Math.round(wy + Math.sin(a) * r);
    R(x, y, 1, 1, ['#ff8ac8', '#8ad0ff', '#ffe08a'][k % 3]);
  }
  R(wx, wy, 1, 1, '#fff0d0');
}

/* ================= Small lights: city windows, the cathedral's candlelight, the belfry, frost on the snow ================= */
function drawCityWins(t) { for (const w of CITY_WINS) if (hash(w.x, w.y, step(t, 6 * w.k)) < 0.5) R(w.x, w.y, 1, 1, w.ph < 0.5 ? '#e8b468' : '#f6dca0'); }
function drawCathWins(t) {
  const s0 = step(t, 480);
  for (const w of CATH_WINS) {
    const s = hash(w.x, w.y, s0 + Math.floor(w.ph * 7));
    if (s > 0.3) continue;
    ctx.globalAlpha = 0.32; R(w.x, w.y + 1, w.w, w.h - 1, '#9a5a2a');
  }
  ctx.globalAlpha = 1;
}
function drawBelfry(t) {
  const lit = env(t, BELFRY[0], BELFRY[1], 3), g = bellGlow(t);
  if (lit <= 0) return;
  halo(HL.belfry, BELL.x, 108, lit * 0.5 + g * 0.6);
  ctx.globalAlpha = lit * 0.85;
  for (const ax of [BELL.x - 8, BELL.x + 3]) R(ax + 1, 104, 5, 11, '#e8a050');
  ctx.globalAlpha = 1;
  const sw = g > 0.4 ? (Math.floor(g * 6) & 1 ? 1 : -1) : 0;               // the bells swing as they strike
  for (const ax of [BELL.x - 8, BELL.x + 3]) { R(ax + 2 + sw, 107, 2, 4, '#7a5224'); R(ax + 2 + sw, 107, 1, 3, '#d8a050'); }
}
function drawGlints(t) {
  const n = GLINTS.length;
  ctx.fillStyle = '#f2f6ff';
  for (let i = 0; i < 260; i++) {
    const { cyc, u } = cycle(t, 240, hash(i, 1, 180));                    // each glint lasts a second, somewhere new
    if (u > 0.55) continue;
    const p = GLINTS[Math.floor(hash(i, cyc, 181) * n)];
    ctx.fillStyle = u > 0.12 && u < 0.43 ? '#f2f6ff' : '#b8c4e0';
    ctx.fillRect(p % W, (p / W) | 0, 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Almaty's own snow, at three depths, thickening for a while ================= */
const FLAKES = [
  { n: 200, nMin: 10, nMax: 14, size: 1, col: '#9aa8cc', a: 0.6, amp: 2, land: [40, HZ] },
  { n: 130, nMin: 16, nMax: 22, size: 1, col: '#d4dcf2', a: 0.85, amp: 4, land: [HZ, 215] },
  { n: 55, nMin: 26, nMax: 34, size: 2, col: '#eef2ff', a: 0.85, amp: 7, land: [200, H + 4] },
].map((L, d) => ({ ...L, f: Array.from({ length: Math.round(L.n * 2.2) }, (_, i) => ({ n: L.nMin + Math.floor(hash(i, d, 171) * (L.nMax - L.nMin)), ph: hash(i, d, 172), s: i * 7 + d })) }));
function drawSnow(depth, t) {
  const L = FLAKES[depth], count = L.n + Math.round(L.n * 1.2 * env(t, SNOWBURST[0], SNOWBURST[1], 12));
  ctx.fillStyle = L.col; ctx.globalAlpha = L.a;
  for (let i = 0; i < count; i++) {
    const f = L.f[i], { cyc, u } = cycle(t, f.n, f.ph), land = lerp(L.land[0], L.land[1], hash(f.s, cyc, 174));
    const y = Math.round(lerp(-6, land, u)), x = Math.round(hash(f.s, cyc, 175) * (W + 30) - 15 + L.amp * Math.sin(TAU * (u * 2 + hash(f.s, 176))));
    ctx.fillRect(x, y, L.size, L.size);
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): Almaty draws its own snow; drizzle, rain, a storm and leaves come from the
// engine, landing on the mountains, the square and the alley, and catching the alley's lamps and the kiosk
const WEATHER = weatherPlans(['snow']);
const WX_LIGHTS = LAMPS.map(L => ({ x: L.x, y: L.base - L.h, rx: L.h * 0.6, ry: L.h * 0.6 })).concat([{ x: 84, y: 176, rx: 34, ry: 22 }]);
const WX = { land: [[60, HZ], [HZ, 200], [196, H + 4]], lights: WX_LIGHTS };
const WX_BB = { ...WX, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y - 4, rx: 18, ry: 14 }] };

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 3.2 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, MTN_C, MTN_PATH, SHADOW_C, MIST_C, KOK_C, TOWER_C, CITY_C, PARK_C, CATH_C, GROUND_C, LAMPS_C,
  BLOCK_C, BB_C, BB_AD, PITCH, KIOSK_C, VIG_C, NEAR_L, NEAR_R, BENCH_C, SNOWMAN_C;
function prerender() {
  HL = {
    moon: makeHalo(48, '#9ab0ff', 0.2, 1.8), moonCore: makeHalo(15, '#fff6e0', 0.32, 1.5),
    tv: makeHalo(30, '#ffe8c8', 0.12, 1.6), tvTop: makeHalo(5, '#ff5a4a', 0.5, 1.3), pod: makeHalo(7, '#ffe0a0', 0.4, 1.3),
    crest: makeHalo(4, '#ffc878', 0.4, 1.3), cabin: makeHalo(3, '#ffe0a0', 0.5, 1.2), crown: makeHalo(10, '#ffd070', 0.3, 1.5),
    cath: makeHalo(78, '#ffc870', 0.15, 1.5), dome: makeHalo(34, '#ffe0a0', 0.16, 1.5), bell: makeHalo(24, '#fff0c8', 0.16, 1.5),
    belfry: makeHalo(26, '#ffb860', 0.35, 1.5), globe: makeHalo(20, '#ffd8a0', 0.34, 1.6), globeCore: makeHalo(5, '#fff4dc', 0.6, 1.3),
    pool: makeHalo(40, '#ffb870', 0.1, 1.4), kiosk: makeHalo(44, '#ffb060', 0.24, 1.5), bulb: makeHalo(3, '#ffd890', 0.5, 1.2),
    city: makeHalo(90, '#ff9a5a', 0.07, 1.3), bbLamp: makeHalo(6, '#ffd8a0', 0.3, 1.6), bbWash: makeHalo(34, '#ffc890', 0.06, 1.5),
    landing: makeHalo(5, '#fff6e0', 0.5, 1.3), show: SHOW_COLS.map(c => makeHalo(30, c, 0.3, 1.5)),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  { const m = renderMountains(); drawHalo(m.getContext('2d'), HL.city, 240, 168, 1); MTN_C = trim(m); } MTN_PATH = mountainPath();
  SHADOW_C = renderBlobs(110, 10, 61, '#0a0e26', [0, 0.2, 0.2], 15);
  MIST_C = renderBlobs(36, 10, 62, '#7a7ea8', [0, 0.32, 0.32], 6);
  KOK_C = trim(renderKokTobe()); TOWER_C = trim(renderTower()); CITY_C = trim(renderCity());
  PARK_C = trim(renderPark()); CATH_C = trim(renderCathedral()); GROUND_C = trim(renderGround()); BENCH_C = trim(BENCH_C); SNOWMAN_C = trim(SNOWMAN_C); LAMPS_C = trim(renderLamps());
  BLOCK_C = trim(renderBlock()); BB_C = trim(renderBillboard()); BB_AD = renderBillboardAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#9bb7d4' });
  KIOSK_C = trim(renderKiosk()); VIG_C = renderVignette();
  NEAR_L = trim((() => { const p = painter(W, H); spruce(p, 16, 262, 124, 31, 0); return p.done(); })());   // spruces at the frame's edges, near us
  NEAR_R = trim((() => { const p = painter(W, H); spruce(p, 466, 240, 104, 33, 0); return p.done(); })());
}
/* ================= Frame compositor (back to front) ================= */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, show = showAt(t);
  // sky, stars, the moon, wisps drifting
  ctx.fillStyle = SKY[SKY.length - 1]; ctx.fillRect(0, 150, W, H - 150);
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) drawScroll(L.c, L.y, L.k, t);
  drawPlane(t);
  // the Alatau, cloud shadows crossing its snowfields, mist on the foothills
  blit(MTN_C);
  ctx.save(); ctx.clip(MTN_PATH); drawScroll(SHADOW_C, 30, 3, t); ctx.restore();
  // Kok-Tobe, the gondola, the wheel, the TV tower and its show
  blit(KOK_C);
  drawKokTobe(t);
  halo(HL.tv, TV.x, 76, 0.9 - show * 0.5);
  blit(TOWER_C);
  halo(HL.pod, TV.x, tvAt(0.65), 0.8);
  drawTowerShow(t, show);
  if (wave(t, 80) > 0.2) { R(TV.x, TV.top - 1, 1, 1, '#ff6a5a'); halo(HL.tvTop, TV.x, TV.top, 0.9); }
  // the city, its windows, the hotel's crown; the block and its sign
  blit(CITY_C);
  drawCityWins(t);
  drawScroll(MIST_C, 116, 4, t);                  // the winter haze lying over the city
  halo(HL.crown, HOTEL.x, HOTEL.top + 2, 0.8);
  blit(BLOCK_C);
  if (billboard) drawBillboard(t, pitch);
  if (wx.snow) drawSnow(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the park's trees and their rooks, the cathedral in its floodlight
  blit(PARK_C);
  halo(HL.cath, 300, 130, 1); halo(HL.dome, MAIN.x, 80, 0.9); halo(HL.bell, BELL.x, 70, 0.7);
  blit(CATH_C);
  drawCathWins(t);
  drawBelfry(t);
  drawRooks(t);
  // the square and the alley, the cathedral mirrored in the swept stones, frost glinting on the snow
  blit(GROUND_C);
  ctx.save(); ctx.beginPath();
  ctx.moveTo(226, HZ); ctx.lineTo(370, HZ); ctx.lineTo(370, PATH.y0 + 1);
  for (let y = PATH.y0 + 1; y <= H; y += 4) ctx.lineTo(pathC(y) + pathW(y) * 0.42, y);
  for (let y = H; y >= PATH.y0 + 1; y -= 4) ctx.lineTo(pathC(y) - pathW(y) * 0.42, y);
  ctx.lineTo(226, PATH.y0 + 1); ctx.closePath(); ctx.clip();
  reflect(ctx, t, { top: HZ + 1, rows: 60, squash: 1.7, alpha: 0.3, amp: 0.4, k1: 30, k2: 70, x: 124, w: 248 });
  ctx.restore();
  streak(ctx, t, MAIN.x, 6, '#ffd890', 0.2, 3, PATH.y0 + 2, 214);
  drawGlints(t);
  if (wx.snow) drawSnow(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the lamps, the kiosk and everyone near, in feet order
  blit(LAMPS_C);
  for (const L of LAMPS) { const [[gx, gy]] = globes(L); halo(HL.globe, gx, gy + Math.round(L.h * 0.08), 0.6 + L.h / 100); for (const [cx, cy] of globes(L)) halo(HL.globeCore, cx, cy, 0.8); }
  halo(HL.kiosk, (KIOSK.x0 + KIOSK.x1) >> 1, KIOSK.top + 26, 1);
  const list = [], put = (y, fn) => list.push([y, list.length, fn]);
  put(KIOSK.base - 0.5, () => {
    blit(KIOSK_C);
    drawVendor(t);
    steam(URN.x + 2, KW.y0 + 2, t, 7);
    KIOSK_BULBS.forEach((x, i) => {
      const y = KIOSK.top + 7 + ((i & 1) ? 1 : 0), a = 0.6 + 0.4 * wave(t, 20 + (i % 4) * 5, i * 0.2);
      R(x, y, 1, 1, ['#ffd890', '#ff8a7a', '#9ad8ff', '#ffe8b0'][i % 4]); halo(HL.bulb, x, y, a);
    });
    for (let k = 0; k < 6; k++) {
      const u = frac(t * 20 / LOOP + k / 6);
      ctx.globalAlpha = 0.28 * (1 - u); R(KIOSK.x1 - 10 + Math.round(u * 10 + Math.sin(u * 7 + k) * 1.5), KIOSK.top - 18 - Math.round(u * 22), 2, 2, '#8a92b0');
    }
    ctx.globalAlpha = 1;
  });
  put(BENCH.y + 6, () => blit(BENCH_C));
  put(SNOWMAN.base, () => blit(SNOWMAN_C));
  people(t, crowd, put);
  list.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  for (const [, , fn] of list) fn();
  blit(NEAR_L); blit(NEAR_R);
  if (wx.snow) drawSnow(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.5;
export default {
  id: 'almaty',
  name: 'Almaty',
  tagline: 'Zenkov under the snowy peaks',
  timeZone: 'Asia/Almaty',
  accent: '#9bb7d4',
  focusX: 300,
  landmark: [CA.x0 - 6, CA.x1 + 6],                // the Ascension Cathedral, apse to west wing
  landmarkTop: CA.tip,                             // the bell tower's cross
  billboard: BB,
  crowd: CROWD,
  spots: [                                         // what answers a tap (site/js/ui/spots.js)
    { kind: 'bell', x: BELL.x - 13, y: CA.tip, w: 27, h: 70 },                                   // the bell tower
    { kind: 'landmark', x: TV.x - 5, y: TV.top - 1, w: 11, h: TV.foot - TV.top + 2 },             // Kok-Tobe's TV tower
    { kind: 'window', x: KW.x0, y: KW.y0, w: KW.x1 - KW.x0 + 1, h: KW.y1 - KW.y0 + 1 },             // the kiosk's window
    { kind: 'birds', x: 393, y: 139, w: 26, h: 15 },                                              // rooks in the bare tree
  ],
  ambience: { wind: 0.2, traffic: 0.12, crowd: 0.12 },
  events: [
    ...BELLS.map(t => ({ t, type: 'bell', x: BELL.x, gain: 0.7 })),
    { t: 24, type: 'chime', x: TV.x, gain: 0.35 }, { t: 144, type: 'chime', x: TV.x, gain: 0.35 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
